// src/api/index.ts
// One function per API endpoint, grouped by domain

import apiClient from './client';
import type {
  User, AuthTokens, Habit, HabitGroups, CreateHabitInput,
  HabitLog, DayEntry, CreateLogInput,
  MonthlyScore, MonthSummary, WinDay, Reflection,
  Penalty, FamilyFund,
} from '../types';

// ── HELPER ────────────────────────────────────────────────────────

async function call<T>(fn: () => Promise<any>): Promise<T> {
  const response = await fn();
  return response.data.data as T;
}

// ════════════════════════════════════════════════════════════════
// AUTH
// ════════════════════════════════════════════════════════════════

export const authApi = {
  login: (username: string, password: string) =>
    call<{ accessToken: string; refreshToken: string; user: User }>(() =>
      apiClient.post('/auth/login', { username, password })
    ),

  logout: (userId: string, refreshToken: string) =>
    call<{ message: string }>(() =>
      apiClient.post('/auth/logout', { userId, refreshToken })
    ),

  forgotPassword: (email: string) =>
    call<{ message: string }>(() =>
      apiClient.post('/auth/forgot-password', { email })
    ),

  resetPassword: (userId: string, resetToken: string, newPassword: string) =>
    call<{ message: string }>(() =>
      apiClient.post('/auth/reset-password', { userId, resetToken, newPassword })
    ),
};

// ════════════════════════════════════════════════════════════════
// USERS
// ════════════════════════════════════════════════════════════════

export const usersApi = {
  getMe: () =>
    call<{ user: User }>(() => apiClient.get('/users/me')),

  updateMe: (updates: Partial<Pick<User, 'name' | 'timezone'>> & {
    currentPassword?: string;
    newPassword?: string;
  }) =>
    call<{ user: User }>(() => apiClient.put('/users/me', updates)),

  inviteMember: (email: string, name: string, username: string) =>
    call<{ user: User }>(() =>
      apiClient.post('/users/invite', { email, name, username })
    ),

  listFamily: () =>
    call<{ members: User[] }>(() => apiClient.get('/users/family')),

  archiveMember: (userId: string) =>
    call<{ message: string }>(() => apiClient.delete(`/users/${userId}`)),
};

// ════════════════════════════════════════════════════════════════
// HABITS
// ════════════════════════════════════════════════════════════════

export const habitsApi = {
  list: () =>
    call<{ habits: HabitGroups }>(() => apiClient.get('/habits')),

  create: (input: CreateHabitInput) =>
    call<{ habit: Habit }>(() => apiClient.post('/habits', input)),

  update: (habitId: string, updates: Partial<CreateHabitInput>) =>
    call<{ habit: Habit }>(() => apiClient.put(`/habits/${habitId}`, updates)),

  delete: (habitId: string) =>
    call<{ message: string }>(() => apiClient.delete(`/habits/${habitId}`)),

  activate: (habitId: string) =>
    call<{ habit: Habit }>(() => apiClient.post(`/habits/${habitId}/activate`)),

  deactivate: (habitId: string) =>
    call<{ habit: Habit }>(() => apiClient.post(`/habits/${habitId}/deactivate`)),

  setPriorities: (habitIds: string[], yearMonth: string) =>
    call<{ priorityHabitIds: string[] }>(() =>
      apiClient.post('/habits/priority', { habitIds, yearMonth })
    ),

  setIntention: (habitId: string, yearMonth: string) =>
    call<{ habit: Habit }>(() =>
      apiClient.post('/habits/intention', { habitId, yearMonth })
    ),
};

// ════════════════════════════════════════════════════════════════
// LOGS
// ════════════════════════════════════════════════════════════════

export const logsApi = {
  getByDate: (date: string) =>
    call<{ date: string; entries: DayEntry[] }>(() =>
      apiClient.get('/logs', { params: { date } })
    ),

  create: (input: CreateLogInput) =>
    call<{ log: HabitLog }>(() => apiClient.post('/logs', input)),

  update: (logId: string, updates: Partial<CreateLogInput>) =>
    call<{ log: HabitLog }>(() => apiClient.put(`/logs/${logId}`, updates)),
};

// ════════════════════════════════════════════════════════════════
// SCORING
// ════════════════════════════════════════════════════════════════

export const scoringApi = {
  getMonthly: (month: string) =>
    call<MonthlyScore>(() =>
      apiClient.get('/scoring/monthly', { params: { month } })
    ),

  getSummary: () =>
    call<{ months: MonthSummary[] }>(() => apiClient.get('/scoring/summary')),

  getWinLog: () =>
    call<{ wins: WinDay[]; totalWins: number }>(() =>
      apiClient.get('/scoring/winlog')
    ),

  saveReflection: (
    yearMonth: string,
    whatWorked: string,
    whatGotInTheWay: string,
    oneThing: string
  ) =>
    call<{ reflection: Reflection }>(() =>
      apiClient.post('/scoring/reflection', {
        yearMonth,
        whatWorked,
        whatGotInTheWay,
        oneThing,
      })
    ),

  getReflection: (month: string) =>
    call<{ reflection: Reflection | null }>(() =>
      apiClient.get('/scoring/reflection', { params: { month } })
    ),
};

// ════════════════════════════════════════════════════════════════
// PENALTIES
// ════════════════════════════════════════════════════════════════

export const penaltiesApi = {
  getMine: (from: string, to: string) =>
    call<{ penalties: Penalty[]; totalPenalty: number; unsettledPenalty: number }>(() =>
      apiClient.get('/penalties', { params: { from, to } })
    ),

  getFamily: (from: string, to: string) =>
    call<FamilyFund>(() =>
      apiClient.get('/penalties/family', { params: { from, to } })
    ),

  settle: (userId: string, from: string, to: string) =>
    call<{ settledCount: number; settledTotal: number }>(() =>
      apiClient.post('/penalties/settle', { userId, from, to })
    ),

  exportCsv: (from: string, to: string, userId?: string) => {
    const params = new URLSearchParams({ from, to });
    if (userId) params.append('userId', userId);
    window.open(`${apiClient.defaults.baseURL}/penalties/export?${params}`, '_blank');
  },
};